import { Globe, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Permission } from '../types';
import {
  groupState,
  MATRIX_COLUMNS,
  setCodes,
  toggleCode,
  type MatrixModule,
  type MatrixResource,
} from '../utils/matrix';

interface PermissionMatrixProps {
  modules: MatrixModule[];
  /** Every resource of the catalog (the "view" rule needs them even when filtered out) */
  allResources: MatrixResource[];
  selected: ReadonlySet<string>;
  saved: ReadonlySet<string>;
  disabled?: boolean;
  onChange: (next: Set<string>) => void;
}

export function PermissionMatrix({ modules, allResources, selected, saved, disabled, onChange }: PermissionMatrixProps) {
  const toggle = (code: string, on: boolean) => onChange(toggleCode(selected, code, on, allResources));
  const setGroup = (codes: string[], on: boolean) => onChange(setCodes(selected, codes, on));

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        {modules.map((mod) => {
          const count = mod.codes.filter((c) => selected.has(c)).length;
          return (
            <Card key={mod.module}>
              <CardHeader className="flex-row items-center gap-3 space-y-0 border-b bg-muted/30 px-4 py-3">
                <Checkbox
                  aria-label={`Todos los permisos de ${mod.module}`}
                  checked={groupState(selected, mod.codes)}
                  disabled={disabled}
                  onCheckedChange={(v) => setGroup(mod.codes, v === true)}
                />
                <h2 className="font-semibold">{mod.module}</h2>
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {count} / {mod.codes.length}
                </span>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th scope="col" className="px-4 py-2 text-left font-medium">Recurso</th>
                      {MATRIX_COLUMNS.map((c) => (
                        <th key={c.key} scope="col" className="w-20 p-2 text-center font-medium">{c.label}</th>
                      ))}
                      <th scope="col" className="px-4 py-2 text-left font-medium">Otras acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mod.resources.map((res) => (
                      <ResourceRow
                        key={res.resource}
                        res={res}
                        selected={selected}
                        saved={saved}
                        disabled={disabled}
                        onToggle={toggle}
                        onRow={(on) => setGroup(res.codes, on)}
                      />
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

interface ResourceRowProps {
  res: MatrixResource;
  selected: ReadonlySet<string>;
  saved: ReadonlySet<string>;
  disabled?: boolean;
  onToggle: (code: string, on: boolean) => void;
  onRow: (on: boolean) => void;
}

function ResourceRow({ res, selected, saved, disabled, onToggle, onRow }: ResourceRowProps) {
  const changed = (code: string) => selected.has(code) !== saved.has(code);

  return (
    <tr className="border-b last:border-0 hover:bg-muted/20">
      <th scope="row" className="px-4 py-2 text-left font-normal">
        <div className="flex items-center gap-3">
          <Checkbox
            aria-label={`Todos los permisos de ${res.label}`}
            checked={groupState(selected, res.codes)}
            disabled={disabled}
            onCheckedChange={(v) => onRow(v === true)}
          />
          <span className="font-medium">{res.label}</span>
          <ScopeHint scoped={res.scoped} />
        </div>
      </th>

      {MATRIX_COLUMNS.map((c) => {
        const p = res.columns[c.key];
        return (
          <td key={c.key} className="p-2 text-center">
            {p ? (
              <CellCheckbox
                permission={p}
                resourceLabel={res.label}
                checked={selected.has(p.code)}
                changed={changed(p.code)}
                disabled={disabled}
                onToggle={onToggle}
              />
            ) : (
              <span className="text-muted-foreground/40" aria-hidden>—</span>
            )}
          </td>
        );
      })}

      <td className="px-4 py-2">
        <div className="flex flex-wrap gap-1.5">
          {res.extras.map((p) => (
            <ExtraToggle
              key={p.code}
              permission={p}
              checked={selected.has(p.code)}
              changed={changed(p.code)}
              disabled={disabled}
              onToggle={onToggle}
            />
          ))}
        </div>
      </td>
    </tr>
  );
}

function ScopeHint({ scoped }: { scoped: boolean }) {
  const Icon = scoped ? MapPin : Globe;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground" tabIndex={0} aria-label={scoped ? 'Por territorio' : 'Global'}>
          <Icon className="size-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {scoped
          ? 'Por territorio: si el rol se asigna a un distrito o región, solo aplica ahí.'
          : 'Global: aplica en todo el país, aunque el rol se asigne a un distrito o región.'}
      </TooltipContent>
    </Tooltip>
  );
}

interface PermissionControlProps {
  permission: Permission;
  checked: boolean;
  changed: boolean;
  disabled?: boolean;
  onToggle: (code: string, on: boolean) => void;
}

function CellCheckbox({ permission: p, resourceLabel, checked, changed, disabled, onToggle }: PermissionControlProps & { resourceLabel: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex rounded p-1', changed && 'bg-warning/20 ring-1 ring-warning/50')}>
          <Checkbox
            aria-label={`${p.action_label} ${resourceLabel}`}
            checked={checked}
            disabled={disabled}
            onCheckedChange={(v) => onToggle(p.code, v === true)}
          />
        </span>
      </TooltipTrigger>
      {p.description && <TooltipContent>{p.description}</TooltipContent>}
    </Tooltip>
  );
}

function ExtraToggle({ permission: p, checked, changed, disabled, onToggle }: PermissionControlProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => onToggle(p.code, !checked)}
          className={cn(
            'rounded-full border px-2.5 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
            checked
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground hover:bg-muted',
            changed && 'ring-2 ring-warning/60',
          )}
        >
          {p.action_label}
        </button>
      </TooltipTrigger>
      {p.description && <TooltipContent>{p.description}</TooltipContent>}
    </Tooltip>
  );
}
